import { useEffect, useRef, useState } from "react";
import { FileDown } from "lucide-react";
import { toast } from "sonner";
import { api, tl, signedTl, todayStr, monthStr, fmtDate, shortDate } from "../lib/api";
import { exportPdf } from "../lib/pdf";
import { useBusiness } from "../context/BusinessContext";
import { DiffBadge } from "../components/DiffBadge";
import { PageHeader, card, btnPrimary } from "../components/PageHeader";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

function ReportHead({ business, title, sub }) {
  return (
    <div className="flex items-center justify-between border-b border-slate-200 pb-4">
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-cyan-700">{title}</div>
        <div className="font-heading text-2xl font-bold text-slate-900">{business}</div>
        <div className="text-sm text-slate-500">{sub}</div>
      </div>
      <img src="/img/kasamnet-logo.jpg" alt="" className="h-20 object-contain mix-blend-multiply" />
    </div>
  );
}

function Row({ label, value, cls = "" }) {
  return (
    <div className={`flex justify-between rounded-lg px-3 py-2 text-sm ${cls}`}>
      <span>{label}</span><span className="font-mono font-semibold">{value}</span>
    </div>
  );
}

function SimpleTable({ title, rows, tone, total, testId }) {
  const head = tone === "green" ? "bg-emerald-600" : "bg-rose-600";
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200" data-testid={testId}>
      <div className={`flex justify-between px-4 py-2.5 text-sm font-bold text-white ${head}`}><span>{title}</span><span className="font-mono">{tl(total)}</span></div>
      <table className="w-full text-sm">
        <tbody>
          {rows.length === 0 && <tr><td className="px-4 py-4 text-center text-slate-400">Kayıt yok</td></tr>}
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-slate-100">
              <td className="px-4 py-2 text-slate-800">{r.name}{r.note ? <span className="text-xs text-slate-500"> · {r.note}</span> : null}</td>
              <td className="px-4 py-2 text-right font-mono font-semibold">{tl(r.amount ?? r.total)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DailyReport({ bid, name }) {
  const [date, setDate] = useState(todayStr());
  const [d, setD] = useState(null);
  const ref = useRef(null);
  const [regs, setRegs] = useState([]);
  useEffect(() => {
    api.get(`/businesses/${bid}/day`, { params: { date } }).then((r) => setD(r.data));
  }, [bid, date]);
  useEffect(() => {
    api.get(`/businesses/${bid}/registers`).then((r) => setRegs(r.data));
  }, [bid]);
  const pdf = async () => {
    await exportPdf(ref.current, `kasamnet-gunluk-${date}.pdf`);
    toast.success("PDF indirildi");
  };
  if (!d) return null;
  const s = d.summary;
  const counts = d.counts || {};
  const map = (arr) => arr.map((e) => ({ name: e.category_name, note: e.note, amount: e.amount }));
  const opening = d.entries.filter((e) => e.is_opening);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <Input data-testid="report-date-input" type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} className="h-10 w-44 rounded-xl" />
        <button data-testid="export-daily-pdf-button" onClick={pdf} className={btnPrimary}><FileDown className="h-4 w-4" /> PDF İndir</button>
      </div>
      <div ref={ref} className={`${card} space-y-5`} data-testid="daily-report">
        <ReportHead business={name} title="Günlük Rapor" sub={fmtDate(date)} />
        <div className="grid gap-4 md:grid-cols-2">
          <SimpleTable testId="daily-income-table" title="Girdiler" tone="green" rows={map(d.entries.filter((e) => e.type === "income" && !e.is_opening))} total={s.income} />
          <SimpleTable testId="daily-expense-table" title="Çıktılar" tone="red" rows={map(d.entries.filter((e) => e.type === "expense"))} total={s.expense} />
        </div>
        <div className="grid gap-1 rounded-xl bg-slate-50 p-2">
          <Row label="Kasa Açılış" value={tl(s.opening)} cls="text-sky-700" />
          {opening.length > 1 && <div className="px-3 text-xs text-slate-400">{opening.length} açılış kaydı</div>}
          <Row label="Toplam Girdi" value={tl(s.income)} cls="text-emerald-700" />
          <Row label="Toplam Çıktı" value={tl(s.expense)} cls="text-rose-700" />
          <Row label="Net (Girdi − Çıktı)" value={signedTl(s.net)} />
        </div>
        <div className="overflow-hidden rounded-xl border border-slate-200" data-testid="daily-counted-table">
          <div className="bg-slate-900 px-4 py-2.5 text-sm font-bold text-white">Sayılan Kasa Detayı</div>
          <table className="w-full text-sm">
            <tbody>
              {regs.map((r) => (
                <tr key={r.id} className="border-t border-slate-100" data-testid={`daily-counted-row-${r.id}`}>
                  <td className="px-4 py-2 text-slate-800">{r.name}</td>
                  <td className="px-4 py-2 text-right font-mono font-semibold">{counts[r.id] == null ? "Sayılmadı" : tl(counts[r.id])}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold">
                <td className="px-4 py-2">Toplam Sayılan Kasa</td>
                <td className="px-4 py-2 text-right font-mono" data-testid="daily-counted-total">{s.counted_cash == null ? "—" : tl(s.counted_cash)}</td>
              </tr>
              <tr className="border-t border-slate-100">
                <td className="px-4 py-2">Beklenen Kasa</td>
                <td className="px-4 py-2 text-right font-mono">{tl(s.expected_cash)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="grid gap-1 rounded-xl bg-slate-50 p-2">
          <div className="flex items-center justify-between px-3 py-2 text-sm"><span>Kasa Durumu</span><DiffBadge diff={s.difference} testId="daily-report-diff" /></div>
        </div>
      </div>
    </div>
  );
}

function MonthlyReport({ bid, name }) {
  const [month, setMonth] = useState(monthStr());
  const [r, setR] = useState(null);
  const ref = useRef(null);
  useEffect(() => {
    api.get(`/businesses/${bid}/reports/monthly`, { params: { month } }).then((x) => setR(x.data));
  }, [bid, month]);
  if (!r) return null;
  const t = r.totals;
  const label = new Date(`${month}-01T00:00:00`).toLocaleDateString("tr-TR", { month: "long", year: "numeric" });
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <Input data-testid="report-month-input" type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} className="h-10 w-44 rounded-xl" />
        <button data-testid="export-monthly-pdf-button" onClick={() => exportPdf(ref.current, `kasamnet-aylik-${month}.pdf`)} className={btnPrimary}><FileDown className="h-4 w-4" /> PDF İndir</button>
      </div>
      <div ref={ref} className={`${card} space-y-5`} data-testid="monthly-report">
        <ReportHead business={name} title="Aylık Rapor" sub={label} />
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {[["Aylık Ciro", tl(t.income), "bg-emerald-50 text-emerald-700", "monthly-income"], ["Toplam Gider", tl(t.expense), "bg-rose-50 text-rose-700", "monthly-expense"],
            ["Toplam Kasa Açığı", `${tl(t.deficit)} (${t.deficit_days} gün)`, "bg-rose-100 text-rose-800", "monthly-deficit"], ["Toplam Kasa Fazlası", `${tl(t.surplus)} (${t.surplus_days} gün)`, "bg-emerald-100 text-emerald-800", "monthly-surplus"]].map(([l, v, c, id]) => (
            <div key={l} className={`rounded-xl p-3 ${c}`} data-testid={id}>
              <div className="text-[11px] font-semibold uppercase tracking-wider opacity-80">{l}</div>
              <div className="mt-1 font-mono text-base font-bold sm:text-lg">{v}</div>
            </div>
          ))}
        </div>
        <div className="text-sm text-slate-600">Net kâr/zarar: <b className="font-mono">{signedTl(t.net)}</b> · Kasa açılış tutarları ciroya dahil edilmemiştir.</div>
        <div className="grid gap-4 md:grid-cols-2">
          <SimpleTable testId="monthly-income-categories" title="Girdiler (Kategori)" tone="green" rows={r.income_categories} total={t.income} />
          <SimpleTable testId="monthly-expense-categories" title="Çıktılar (Kategori)" tone="red" rows={r.expense_categories} total={t.expense} />
        </div>
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full min-w-[560px] text-sm" data-testid="monthly-days-table">
            <thead className="bg-slate-900 text-left text-xs uppercase tracking-wider text-white">
              <tr><th className="px-3 py-2">Gün</th><th className="px-3 py-2 text-right">Açılış</th><th className="px-3 py-2 text-right">Girdi</th><th className="px-3 py-2 text-right">Çıktı</th><th className="px-3 py-2 text-right">Kasa Farkı</th></tr>
            </thead>
            <tbody>
              {r.days.length === 0 && <tr><td colSpan={5} className="px-3 py-4 text-center text-slate-400">Bu ay kayıt yok</td></tr>}
              {r.days.map((d) => (
                <tr key={d.date} className={`border-t border-slate-100 ${d.difference < 0 ? "bg-rose-50" : d.difference > 0 ? "bg-emerald-50" : ""}`}>
                  <td className="px-3 py-2">{shortDate(d.date)}</td>
                  <td className="px-3 py-2 text-right font-mono text-sky-700">{tl(d.opening)}</td>
                  <td className="px-3 py-2 text-right font-mono text-emerald-700">{tl(d.income)}</td>
                  <td className="px-3 py-2 text-right font-mono text-rose-700">{tl(d.expense)}</td>
                  <td className={`px-3 py-2 text-right font-mono font-bold ${d.difference < 0 ? "text-rose-700" : d.difference > 0 ? "text-emerald-700" : "text-slate-500"}`}>
                    {d.difference == null ? "—" : signedTl(d.difference)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function Reports() {
  const { current } = useBusiness();
  if (!current) return null;
  return (
    <div className="space-y-6" data-testid="reports-page">
      <PageHeader title="Raporlar" subtitle="Günlük ve aylık raporlarınızı görüntüleyin, PDF olarak indirin." />
      <Tabs defaultValue="daily">
        <TabsList className="rounded-xl">
          <TabsTrigger value="daily" data-testid="daily-report-tab">Günlük Rapor</TabsTrigger>
          <TabsTrigger value="monthly" data-testid="monthly-report-tab">Aylık Rapor</TabsTrigger>
        </TabsList>
        <TabsContent value="daily" className="mt-4"><DailyReport bid={current.id} name={current.name} /></TabsContent>
        <TabsContent value="monthly" className="mt-4"><MonthlyReport bid={current.id} name={current.name} /></TabsContent>
      </Tabs>
    </div>
  );
}
